/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';
import { TableHead, TableRow, Typography, TableBody, Grid, TablePagination, CircularProgress } from '@mui/material';
import { SBTableRow } from '@Components/Table/TableRow';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import { SBTable, SBTableWrapper } from '@Components/Table/Table';

export interface Column {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: string | number;
  render?: (row: any, index?: number) => React.ReactNode;
}

export interface ReusableTableProps {
  columns: Column[];
  rows: any[];
  actions?: (row: any) => React.ReactNode;
  emptyMessage?: string;
  pagination?: boolean;
  rowsPerPageOptions?: number[];
  totalCount?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
}

/**
 * ✅ ReusableTable styled using SBTable, with center-aligned cells
 */
const ReusableTable: React.FC<ReusableTableProps> = ({
  columns,
  rows,
  actions,
  emptyMessage = 'No Data Found.',
  pagination = false,
  rowsPerPageOptions = [5, 10, 25],
  totalCount = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
}) => {
  return (
    <SBTableWrapper>
      <SBTable>
        {/* ===== Table Header ===== */}
        <TableHead>
          <TableRow>
            {columns.map(col => (
              <TableHeadCell key={col.id} align={col.align || 'center'} sx={{ minWidth: col.minWidth }}>
                {col.label}
              </TableHeadCell>
            ))}
            {actions && (
              <TableHeadCell
                className="md-text"
                align="center"
                sx={{
                  minWidth: '150px', // ✅ Increased width
                  width: '150px',
                }}
              >
                Actions
              </TableHeadCell>
            )}
          </TableRow>
        </TableHead>

        {/* ===== Table Body ===== */}
        <TableBody>
          {loading ? (
            <SBTableRow>
              <TableRowCell
                colSpan={columns.length + (actions ? 1 : 0)}
                align="center"
                sx={{
                  height: 200,
                  border: 'none',
                  textAlign: 'center',
                }}
              >
                <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                  <CircularProgress color="primary" />
                </Grid>
              </TableRowCell>
            </SBTableRow>
          ) : rows?.length > 0 ? (
            rows.map((row, rowIndex) => (
              <SBTableRow key={rowIndex}>
                {columns.map(col => (
                  <TableRowCell
                    key={col.id}
                    align={col.align || 'center'}
                    sx={{
                      border: 'none',
                      minWidth: col.minWidth,
                      textAlign: col.align || 'center',
                      verticalAlign: 'middle',
                    }}
                    className="md-text"
                  >
                    {col.render ? col.render(row, rowIndex) : row[col.id]}
                  </TableRowCell>
                ))}
                {actions && (
                  <TableRowCell
                    align="center"
                    sx={{
                      border: 'none !important',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '1rem',
                      height: '100%',
                    }}
                  >
                    <Grid container justifyContent="center" columnSpacing={1}>
                      {actions(row)}
                    </Grid>
                  </TableRowCell>
                )}
              </SBTableRow>
            ))
          ) : (
            <SBTableRow>
              <TableRowCell colSpan={columns.length + (actions ? 1 : 0)}>
                <Typography className="sb-text-gray font-medium text-center">{emptyMessage}</Typography>
              </TableRowCell>
            </SBTableRow>
          )}
        </TableBody>
      </SBTable>

      {/* ===== Pagination ===== */}
      {pagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange || (() => {})}
          onRowsPerPageChange={onRowsPerPageChange || (() => {})}
        />
      )}
    </SBTableWrapper>
  );
};

export default ReusableTable;
